/**
 * This is a standalone file because I don't want to import
 * lots of unused code into main.ts (by having it in import-utils.ts).
 */

import { assert, checkExhaustive } from "@src/util/assert";
import { RouteData, RouteFileType } from "@src/util/route-data";

export function supportsRefresh(routeData: RouteData): boolean {
  // If it was already imported via URL, then just assume that still works.
  if (routeData.importedViaUrl) return true;

  // See if we have support for the attached link.
  // The link is typically a link to the map itself (like a Ride With GPS route) and not the export file.
  const link = routeData.link;
  if (!link) return false;
  const fileType = routeData.fileType;
  switch (fileType) {
    case RouteFileType.KMZ:
      try {
        return Boolean(getGoogleMyMapsUrlMid(new URL(link)));
      } catch {
        return false;
      }
    case RouteFileType.GPX:
      return false;
    default:
      checkExhaustive(fileType);
      return false;
  }
}

/**
 * Example URLs:
 * https://www.google.com/maps/d/u/0/edit?mid=1k2YlZFKxCekCzaxeCxQsPwceApNGbNZa&ll=32.91884253789883%2C-88.40991999999999&z=6
 * https://www.google.com/maps/d/viewer?mid=19JrqOOrIaxygQK2iKlS5044-h1GTe10A&ll=39.66880370347725%2C-78.49961499999999&z=8
 * https://www.google.com/mymaps/viewer?mid=19JrqOOrIaxygQK2iKlS5044-h1GTe10A
 * @throws If it's a google maps URL but missing a required param.
 */
export function getGoogleMyMapsUrlMid(url: URL): string|null {
  if (url.origin !== 'https://www.google.com') {
    return null;
  }
  if (!(url.pathname.startsWith('/maps/d/') ||
         url.pathname.startsWith('/mymaps/'))) {
    return null;
  }
  const mid = url.searchParams.get('mid');
  assert(mid, 'Missing required "mid" param in Google My Maps URL.');
  return mid;
}